import React from 'react';
import ismobile from 'is-mobile';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Dialog from '@material-ui/core/Dialog';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Alert from '@material-ui/lab/Alert';
import IconButton from '@material-ui/core/IconButton';
import Webcam from 'webcam-easy';
import Cropper from 'react-easy-crop';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import 'react-easy-crop/react-easy-crop.css';

const styles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  input: {
    display: 'none',
  },
  textRight: {
    textAlign: 'right',
  },
  camera: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    '-webkit-transform': 'scaleX(1) !important',
    transform: 'scaleX(1) !important',
  },
  snapButton: {
    backgroundColor: '#333',
    color: 'white',
    borderRadius: '100px',
    padding: 0,
    width: '50px',
    height: '50px',
    '-webkit-box-shadow': '0 0 10px #fff',
    boxShadow: '0 0 10px #fff',
    border: 'none',
  },
  rotateButton: {
    backgroundColor: '#333',
    padding: 0,
    width: '40px',
    height: '40px',
    '-webkit-box-shadow': '0 0 10px #fff',
    boxShadow: '0 0 10px #fff',
    border: 'none',
  },
  faceMaskOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    zIndex: 1,
  },
  cameraContainer: {
    position: 'relative',
    width: 'min(85vw, 420px)',
    height: 'min(85vw, 420px)',
    overflow: 'hidden',
    borderRadius: 12,
    margin: '0 auto',
  },
  shadeOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.45)',
  },
  personMask: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    opacity: 0.55,
    pointerEvents: 'none',
  },
  faceMaskText: {
    position: 'absolute',
    top: '10%',
    left: '50%',
    transform: 'translateX(-50%)',
    color: 'white',
    fontSize: '14px',
    textShadow: '0 0 4px rgba(0, 0, 0, 0.8)',
    textAlign: 'center',
    padding: '0 20px',
  },
  cropContainer: {
    position: 'relative',
    width: 'min(85vw, 420px)',
    height: 'min(85vw, 420px)',
    margin: '0 auto',
  },
  cropGuideOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.8))',
  },
}));

const dataURLtoFile = (dataurl, filename) => {
  let arr = dataurl.split(','),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};

function rotateBase64Image90deg(base64Image, isClockwise, cb) {
  // create an off-screen canvas
  const offScreenCanvas = document.createElement('canvas');
  const offScreenCanvasCtx = offScreenCanvas.getContext('2d');

  // cteate Image
  var img = new Image();
  img.src = base64Image;

  img.onload = function () {
    // set its dimension to rotated size
    offScreenCanvas.height = img.height;
    offScreenCanvas.width = img.width;

    // rotate and draw source image into the off-screen canvas:
    offScreenCanvasCtx.scale(-1, 1);
    offScreenCanvasCtx.drawImage(img, -img.width, 0);

    // encode image to data-uri with base64
    return cb(offScreenCanvas.toDataURL());
  };
}

const getCroppedImg = (image, croppedAreaPixels, filename, type) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = croppedAreaPixels.width;
  canvas.height = croppedAreaPixels.height;

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    croppedAreaPixels.width,
    croppedAreaPixels.height
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        blob.name = filename;
        resolve(blob);
      },
      type,
      0.85,
    );
  });
};

const UploadImageDialog = (props) => {
  const { onConfirm, opened, handleClose, btnLabel, maskSrc } = props;
  const { t } = useTranslation();
  const imgRef = React.useRef(null);
  const webcamRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const rotationCanvas = React.useRef(null);
  const cameraBoxRef = React.useRef(null);
  const [facingMode, setFacingMode] = React.useState('user');
  const [webcam, setWebcam] = React.useState(null);
  const [file, setFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState(null);
  const [devices, setDevices] = React.useState(0);
  const classes = styles();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  const restart = () => {
    setFile(null);
    setPreview(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);

    if (webcam) {
      webcam.stop();
    }
  };
  const onClose = () => {
    restart();
    handleClose();
  };

  const listDevices = async () => {
    const d = await navigator.mediaDevices.enumerateDevices();

    const video = d.filter((dd) => dd.kind === 'videoinput');

    setDevices(video.length);
  };

  const startWebcam = async (mode) => {
    try {
      restart();

      const fm = mode || facingMode;
      const cam = new Webcam(webcamRef.current, fm, canvasRef.current);

      await cam.start();

      setWebcam(cam);
    } catch (err) {
      console.error(err);
    }
  };

  const takeSnap = async () => {
    if (!webcam) {
      await startWebcam();
    }

    const video = webcamRef.current;
    const box = cameraBoxRef.current;
    const cw = box ? box.offsetWidth : 420;
    const ch = box ? box.offsetHeight : 420;
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');

    const vw = video.videoWidth || video.clientWidth || cw;
    const vh = video.videoHeight || video.clientHeight || ch;
    const scale = Math.max(cw / vw, ch / vh);
    const dw = vw * scale;
    const dh = vh * scale;
    const dx = (cw - dw) / 2;
    const dy = (ch - dh) / 2;
    ctx.drawImage(video, dx, dy, dw, dh);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const f = new File([blob], 'camera-capture.png', { type: 'image/png' });
      const reader = new FileReader();
      reader.onloadend = () => {
        setFile(f);
        setPreview(reader.result);
      };
      reader.readAsDataURL(f);
    }, 'image/png', 0.95);
  };

  const flipCamera = async () => {
    try {
      const next = facingMode === 'user' ? 'environment' : 'user';
      setFacingMode(next);
      if (webcam) {
        webcam.stop();
      }
      await startWebcam(next);
    } catch (e) {
      console.error(e);
    }
  };

  React.useEffect(() => {
    setTimeout(() => {
      if (opened && canvasRef && canvasRef.current) {
        listDevices();
        startWebcam(facingMode);
      }
    }, 200);
  }, [opened, facingMode]);

  return (
    <Dialog
      open={opened}
      onClose={onClose}
      fullWidth
      maxWidth={'sm'}
      scroll='body'
      fullScreen={matches}
    >
      <Paper className={classes.paper}>
        <div className={classes.textRight}>
          <IconButton size='small' onClick={onClose}>
            <i className='fa fa-times'></i>
          </IconButton>
        </div>
        {!preview && !webcam && (
          <Alert severity='info'>Allow the website to use your camera to continue</Alert>
        )}
        <Box className={classes.cameraContainer} style={{ display: preview ? 'none' : 'block' }} ref={cameraBoxRef}>
          <canvas ref={rotationCanvas} style={{ display: 'none' }}></canvas>
          <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
          <video ref={webcamRef} autoPlay playsInline className={classes.camera}></video>
          
          {/* Face mask overlay */}
          <div className={classes.faceMaskOverlay}>
            <div className={classes.faceMaskText}>
              {t('Position your face within the crop area')}
            </div>
            {maskSrc && (
              <img src={maskSrc} className={classes.personMask} alt="mask guide" />
            )}
          </div>
          
          <Box textAlign='center' style={{ position: 'absolute', bottom: '20px', left: 0, right: 0 }}>
            <button className={classes.snapButton} type='button' onClick={takeSnap}>
              <i className='fa fa-camera' />
            </button>
          </Box>
          {devices > 0 && (
            <Box style={{ position: 'absolute', right: '16px', bottom: '16px', zIndex: 2 }}>
              <button type='button' className={classes.rotateButton} onClick={flipCamera}>
                <img src='/camera_flip_white.png' width='40px' />
              </button>
            </Box>
          )}
        </Box>
        <Box textAlign='center' mt={2}>
          <img src={preview} ref={imgRef} style={{ display: 'none' }} alt='hidden' />

          {!!preview && (
            <div className={classes.cropContainer}>
              <Cropper
                image={preview}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid={true}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(area, areaPixels) => setCroppedAreaPixels(areaPixels)}
              />
              {maskSrc && (
                <img src={maskSrc} className={classes.cropGuideOverlay} alt="mask guide" />
              )}
            </div>
          )}
        </Box>
        {!!preview && (
          <>
            <Button
              type='button'
              component='label'
              variant='outlined'
              color='primary'
              onClick={startWebcam}
            >
              {t('New picture')}
            </Button>
            &nbsp;
            <Button
              type='button'
              component='label'
              variant='contained'
              color='primary'
              onClick={async () => {
                const blob = await getCroppedImg(
                  imgRef.current,
                  croppedAreaPixels,
                  file.name,
                  file.type
                );

                onConfirm(blob);
                onClose();
              }}
            >
              {!!btnLabel && <>{btnLabel}</>}
              {!btnLabel && <>{t('Confirm and save')}</>}
            </Button>
          </>
        )}
      </Paper>
    </Dialog>
  );
};

export default UploadImageDialog;
